"use client";

import { forwardRef, type SelectHTMLAttributes } from "react";

export type SelectSize = "sm" | "md" | "lg";

interface SelectOption {
  value: string;
  label: string;
}

interface SelectProps extends Omit<SelectHTMLAttributes<HTMLSelectElement>, "size"> {
  selectSize?: SelectSize;
  options: SelectOption[];
  placeholder?: string;
  error?: boolean;
}

const sizeClasses: Record<SelectSize, string> = {
  sm: "h-7 px-2 text-[12px]",
  md: "h-8 px-2.5 text-[13px]",
  lg: "h-10 px-3 text-sm",
};

// GAM Brand v2: select radius + spring (same tokens as Input).
const selectGamStyle = {
  borderRadius: "var(--gam-radius-input, var(--cc-radius, 0.5rem))",
  transitionTimingFunction: "var(--gam-spring-default, cubic-bezier(0.4, 0, 0.2, 1))",
} as const;

const Select = forwardRef<HTMLSelectElement, SelectProps>(
  ({ selectSize = "md", options, placeholder, error, className = "", style, ...rest }, ref) => {
    return (
      <select
        ref={ref}
        style={{ ...selectGamStyle, ...(style || {}) }}
        className={[
          "w-full appearance-none border bg-white/[0.03] text-slate-200 outline-none transition-colors",
          "focus:bg-white/[0.06]",
          "disabled:pointer-events-none disabled:opacity-50",
          error
            ? "border-red-500/40 focus:border-red-400/60"
            : "border-white/[0.08] focus:border-[var(--cc-accent-500-50)]",
          sizeClasses[selectSize],
          className,
        ].join(" ")}
        {...rest}
      >
        {placeholder && (
          <option value="" disabled>
            {placeholder}
          </option>
        )}
        {options.map((opt) => (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        ))}
      </select>
    );
  }
);

Select.displayName = "Select";
export { Select };
